import type { UserRole } from '@src/dto/superDappDetail/shared';
import type { AuthStateAuthenticated } from '../contexts/AuthState';
import type { AuthState } from '../hooks/useAuth';
import { AuthStateType } from '../hooks/useAuth';
export type { UserRole };

export function hasSolanaEmailAddress(
  email: string | null | undefined,
): boolean {
  return email?.trim().toLowerCase().endsWith('@solana.com') ?? false;
}

export function hasAdminAccess(role: UserRole, email?: string | null): boolean {
  return (
    (role === 'admin' || role === 'superadmin') && hasSolanaEmailAddress(email)
  );
}

export function hasSuperadminAccess(
  role: UserRole,
  email?: string | null,
): boolean {
  return role === 'superadmin' && hasAdminAccess(role, email);
}

export function hasHubspotTicketAccess(
  role: UserRole,
  email?: string | null,
): boolean {
  return role === 'moderator' || hasAdminAccess(role, email);
}

export function hasDappStaffAccess(
  role: UserRole,
  email?: string | null,
): boolean {
  return role === 'moderator' || hasAdminAccess(role, email);
}

export function hasPublisherStaffAccess(
  role: UserRole,
  email?: string | null,
): boolean {
  return role === 'moderator' || hasAdminAccess(role, email);
}

export function isAuthenticated(
  authState: AuthState,
): authState is AuthStateAuthenticated {
  return authState.type === AuthStateType.Authenticated;
}

export function canAccessAdminPages(authState: AuthState): boolean {
  return (
    isAuthenticated(authState) &&
    hasAdminAccess(authState.user.role, authState.user.email)
  );
}

export function canAccessPublisherPages(authState: AuthState): boolean {
  return (
    isAuthenticated(authState) &&
    hasPublisherStaffAccess(authState.user.role, authState.user.email)
  );
}

export function canAccessHubspotTicketPages(authState: AuthState): boolean {
  return (
    isAuthenticated(authState) &&
    hasHubspotTicketAccess(authState.user.role, authState.user.email)
  );
}

export function getDisplayRole(
  actualRole: UserRole,
  viewerRole: UserRole,
): UserRole {
  if (viewerRole === 'admin' && actualRole === 'superadmin') {
    return 'admin';
  }
  return actualRole;
}

export function getAvailableRoles(viewerRole: UserRole): UserRole[] {
  if (viewerRole === 'admin') {
    return ['base', 'moderator', 'admin'];
  }
  return ['base', 'moderator', 'admin', 'superadmin'];
}

export function canModifyUserRole(
  targetUserRole: UserRole,
  viewerRole: UserRole,
): boolean {
  if (viewerRole === 'admin' && targetUserRole === 'superadmin') {
    return false;
  }
  return true;
}
